import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { RedeemMethod } from "kadow-jetpack-model";
import { FormikFormChildProps } from "../FormikForm/FormikFormWrapperForJetpackConfig";
import { tt } from "../../../../utils/i18n/i18n";
import KC_Table from "../../../../designsystem/KC_Table/KC_Table";
import KC_Input from "../../../../designsystem/KC_FormInput/KC_Input";
import KC_Checkbox from "../../../../designsystem/KC_FormInput/KC_Checkbox";
import KC_DateTimePicker from "../../../../designsystem/KC_FormInput/KC_DateTimePicker";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import { DEFAULT_TIMEZONE } from "../../../../utils/constants";
import TwemojiWrapper from "../../../../components/TwemojiWrapper/TwemojiWrapper";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_Heading3 from "../../../../designsystem/typography/KC-Heading/KC_Heading3";
import "./settings-form.scss";
import KC_Padder from "../../../../designsystem/KC_Padder/KC_Padder";

const FIVE_MINUTES_IN_SECONDS = 300;
const TEN_MINUTES_IN_SECONDS = 600;
const TWENTY_MINUTES_IN_SECONDS = 1200;
const THIRTY_MINUTES_IN_SECONDS = 1800;
const FORTY_FIVE_MINUTES_IN_SECONDS = 2700;
const ONE_HOUR_IN_SECONDS = 3600;
const TWO_HOURS_IN_SECONDS = 7200;

export default function SettingsForm(props: FormikFormChildProps) {
  const { config, setFieldValue } = props;
  const { accountToDisplay } = useAccountToDisplaySelector();

  const [minTsEnabled, setMinTsEnabled] = useState<boolean>(
    !!config.minTs && config.minTs > 0
  );
  const [maxTsEnabled, setMaxTsEnabled] = useState<boolean>(
    !!config.maxTs && config.maxTs > 0
  );

  const timezone = useMemo(() => {
    return (
      accountToDisplay?.businessUser?.tz || config.timezone || DEFAULT_TIMEZONE
    );
  }, [accountToDisplay?.businessUser?.tz, config.timezone]);

  useEffect(() => {
    if (!config.timezone && accountToDisplay?.businessUser?.tz) {
      setFieldValue("timezone", accountToDisplay.businessUser.tz);
    }
  }, [config.timezone, accountToDisplay?.businessUser?.tz, setFieldValue]);

  const handleRedemptionMethodChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value;

    switch (value) {
      case "code_4_digits":
        setFieldValue("redeemMethod", "code");
        setFieldValue("nhr", false);
        setFieldValue("sr", false);
        break;
      case "soft_redeem":
        setFieldValue("redeemMethod", "code");
        setFieldValue("nhr", true);
        setFieldValue("sr", true);
        break;
      case "qr_code":
        setFieldValue("redeemMethod", "qr");
        setFieldValue("nhr", false);
        setFieldValue("sr", false);
        break;
      default:
        break;
    }
  };

  const currentRedemptionMethod = useMemo(() => {
    if (config.redeemMethod === RedeemMethod.QR) {
      return "qr_code";
    }
    if (config.sr) {
      return "soft_redeem";
    }
    return "code_4_digits";
  }, [config.redeemMethod, config.sr, config.nhr]);

  const handleMinTsCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setMinTsEnabled(checked);

    if (!checked) {
      setFieldValue("minTs", 0);
    } else {
      const now = moment().tz(timezone);
      setFieldValue("minTs", now.unix());
    }
  };

  const handleMaxTsCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setMaxTsEnabled(checked);

    if (!checked) {
      setFieldValue("maxTs", 0);
    } else {
      const now = moment().tz(timezone);
      setFieldValue("maxTs", now.unix());
    }
  };

  const handleMinTsDateChange = (date: Date | null) => {
    if (date) {
      const timestamp = moment(date).unix();
      setFieldValue("minTs", timestamp);
    }
  };

  const handleMaxTsDateChange = (date: Date | null) => {
    if (date) {
      const timestamp = moment(date).unix();
      setFieldValue("maxTs", timestamp);
    }
  };

  const handleBeaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue("bea", e.target.checked);
  };

  const handleRedeemCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,4}$/.test(value)) {
      setFieldValue("redeemCode", value);
    }
  };

  const showRedeemCodeInput = useMemo(() => {
    return (
      config.redeemMethod === "qr" ||
      (config.redeemMethod === "code" && !config.nhr)
    );
  }, [config.redeemMethod, config.nhr]);

  const showSoftRedeemDelay = useMemo(() => {
    return !!config.sr;
  }, [config.sr]);

  const REDEMPTION_METHOD_EXPLANATION = useMemo(() => {
    switch (currentRedemptionMethod) {
      case "code_4_digits":
        return tt("redeem_4_digits_explanation");
      case "soft_redeem":
        return tt("redeem_soft_redeem_explanation");
      case "qr_code":
        return tt("redeem_qr_code_explanation");
    }
  }, [currentRedemptionMethod]);

  const COL_LABEL_STYLE = { minWidth: "30%", width: "30%", maxWidth: "30%" };
  const COL_VALUE_STYLE = { minWidth: "70%", width: "70%", maxWidth: "70%" };

  return (
    <div className="settings-form-container px-4 py-3 d-flex flex-column gap-3">
      <KC_Heading3 text={tt("settings_section_coupons")} className="mb-2" />
      <KC_Table
        keyPrefix="settings-form-coupons"
        border={true}
        className="w-100 mb-5"
      >
        <tr>
          <td style={COL_LABEL_STYLE}>{tt("coupon_redemption_method")}</td>
          <td style={COL_VALUE_STYLE}>
            <div className="w-100 d-flex flex-column gap-sm">
              <select
                className="kc_input"
                value={currentRedemptionMethod}
                onChange={handleRedemptionMethodChange}
                required={true}
              >
                <option value="soft_redeem">
                  {tt("coupon_redemption_soft_redeem")}
                </option>
                <option value="code_4_digits">
                  {tt("coupon_redemption_validation_code")}
                </option>
                <option value="qr_code">
                  {tt("coupon_redemption_qr_code")}
                </option>
              </select>
              <KC_UIText
                weight="reg"
                text={REDEMPTION_METHOD_EXPLANATION}
                className="d-inline-block white-space-normal"
              />
            </div>
          </td>
        </tr>

        {showSoftRedeemDelay && (
          <tr>
            <td>{tt("soft_redeem_delay")}</td>
            <td>
              <select
                className="kc_input"
                value={config.srDelay || FIVE_MINUTES_IN_SECONDS}
                onChange={(e) =>
                  setFieldValue("srDelay", parseInt(e.target.value))
                }
                required={true}
              >
                <option value={FIVE_MINUTES_IN_SECONDS}>
                  {tt("srdelay_5_minutes")}
                </option>
                <option value={TEN_MINUTES_IN_SECONDS}>
                  {tt("srdelay_10_minutes")}
                </option>
                <option value={TWENTY_MINUTES_IN_SECONDS}>
                  {tt("srdelay_20_minutes")}
                </option>
                <option value={THIRTY_MINUTES_IN_SECONDS}>
                  {tt("srdelay_30_minutes")}
                </option>
                <option value={FORTY_FIVE_MINUTES_IN_SECONDS}>
                  {tt("srdelay_45_minutes")}
                </option>
                <option value={ONE_HOUR_IN_SECONDS}>
                  {tt("srdelay_1_hour")}
                </option>
                <option value={TWO_HOURS_IN_SECONDS}>
                  {tt("srdelay_2_hours")}
                </option>
              </select>
            </td>
          </tr>
        )}

        {showRedeemCodeInput && (
          <tr>
            <td>{tt("redeem_code_4_digits")}</td>
            <td>
              <KC_Input
                id="redeemCode"
                htmlFor="redeemCode"
                type="text"
                value={config.redeemCode || ""}
                setFieldValue={setFieldValue}
                onChange={handleRedeemCodeChange}
                placeholder="1234"
                maxLength={4}
                inputMode="numeric"
                required={true}
              />
              {config.redeemCode &&
                config.redeemCode.length !== 4 &&
                !/^\d{4}$/.test(config.redeemCode) && (
                  <div className="text-danger small mt-1">
                    {tt("redeem_code_validation_error")}
                  </div>
                )}
            </td>
          </tr>
        )}
      </KC_Table>

      <KC_Heading3 text={tt("settings_section_dates")} className="mb-2" />
      <KC_Table
        keyPrefix="settings-form-dates"
        border={true}
        className="w-100 mb-5"
      >
        <tr>
          <td style={COL_LABEL_STYLE}>{tt("game_start_date")}</td>
          <td style={COL_VALUE_STYLE}>
            <div className="d-flex flex-column gap-2">
              <KC_Checkbox
                id="minTs-enabled"
                checked={minTsEnabled}
                className="px-0"
                onChange={handleMinTsCheckboxChange}
              />
              {minTsEnabled && (
                <div className="d-flex flex-column">
                  <KC_DateTimePicker
                    activeDate={
                      config.minTs && config.minTs > 0
                        ? moment.unix(config.minTs).toDate()
                        : new Date()
                    }
                    onChange={handleMinTsDateChange}
                    showTimeSelect={true}
                    timeIntervals={15}
                    dateFormat="Pp"
                  />
                  <small className="text-muted mt-1">
                    {tt("timezone")}: {timezone}
                  </small>
                </div>
              )}
            </div>
          </td>
        </tr>

        <tr>
          <td>{tt("game_end_date")}</td>
          <td>
            <div className="d-flex flex-column gap-2">
              <KC_Checkbox
                id="maxTs-enabled"
                checked={maxTsEnabled}
                className="px-0"
                onChange={handleMaxTsCheckboxChange}
              />
              {maxTsEnabled && (
                <div className="d-flex flex-column">
                  <KC_DateTimePicker
                    activeDate={
                      config.maxTs && config.maxTs > 0
                        ? moment.unix(config.maxTs).toDate()
                        : new Date()
                    }
                    onChange={handleMaxTsDateChange}
                    showTimeSelect={true}
                    timeIntervals={15}
                    dateFormat="Pp"
                  />
                  <small className="text-muted mt-1">
                    {tt("timezone")}: {timezone}
                  </small>
                </div>
              )}
            </div>
          </td>
        </tr>
      </KC_Table>

      <KC_Heading3 text={tt("settings_section_display")} className="mb-2" />
      <KC_Table
        keyPrefix="settings-form-display"
        border={true}
        className="w-100"
      >
        <tr>
          <td style={COL_LABEL_STYLE}>{tt("full_screen_mode")}</td>
          <td style={COL_VALUE_STYLE}>
            <div className="d-flex flex-column gap-2">
              <KC_Checkbox
                id="full_screen_mode-enabled"
                checked={config.fs}
                className="px-0"
                onChange={(e) => setFieldValue("fs", e.target.checked)}
              />
              <KC_UIText
                weight="reg"
                className="d-inline-block white-space-normal"
                text={tt("full_screen_desc")}
              />
            </div>
          </td>
        </tr>

        {!!accountToDisplay?.saasFeatures?.aa && (
          <tr>
            <td>{tt("bypass_email_auth")}</td>
            <td>
              <div className="d-flex flex-column gap-2">
                <KC_Checkbox
                  id="bea"
                  checked={config.bea || false}
                  className="px-0"
                  onChange={handleBeaChange}
                />
                {config.bea && (
                  <TwemojiWrapper isText={true}>
                    <div className="d-flex flex-row gap-sm">
                      ⚠
                      <KC_UIText
                        weight="reg"
                        className="d-inline-block white-space-normal"
                        text={tt("bypass_email_auth_warning")}
                      />
                    </div>
                  </TwemojiWrapper>
                )}
              </div>
            </td>
          </tr>
        )}
      </KC_Table>
      <KC_Padder />
    </div>
  );
}
